import React, { cloneElement, useEffect, useLayoutEffect, useRef, useState } from 'react';

import { FontAwesomeIcon } from '@magoosh/lib/icons';

interface Props {
  align?: 'left' | 'right';
  children: any;
  // When set, the menu flips above the trigger if it would overflow the bottom of
  // the viewport, and its height is capped to the available space (scrolling inside).
  fitViewport?: boolean;
}

const Dropdown: (React.FC<Props> & { Caret: any; Chevron: any; Content: any; Trigger: any; }) = (props) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const [placement, setPlacement] = useState<{ openUp: boolean; maxHeight?: number }>({ openUp: false });

  const align = props.align || 'left';

  useLayoutEffect(() => {
    if (!props.fitViewport || !isExpanded) {
      setPlacement({ openUp: false });
      return;
    }

    const measure = () => {
      const container = containerRef.current;
      const menu = menuRef.current;
      if (!container || !menu) return;

      const rect = container.getBoundingClientRect();
      const gutter = 8;
      // The fixed navbar overlays the top of the viewport, so it eats into the
      // space available above the trigger.
      const navbar = document.getElementsByClassName('navbar-fixed-top')[0];
      const navbarHeight = navbar ? navbar.scrollHeight : 0;
      const spaceBelow = window.innerHeight - rect.bottom - gutter;
      const spaceAbove = rect.top - navbarHeight - gutter;
      const naturalHeight = menu.scrollHeight;

      if (naturalHeight <= spaceBelow) {
        setPlacement({ openUp: false, maxHeight: undefined });
      } else if (spaceAbove > spaceBelow) {
        setPlacement({ openUp: true, maxHeight: Math.min(naturalHeight, Math.max(spaceAbove, 0)) });
      } else {
        setPlacement({ openUp: false, maxHeight: Math.min(naturalHeight, Math.max(spaceBelow, 0)) });
      }
    };

    measure();

    window.addEventListener('resize', measure);
    window.addEventListener('scroll', measure, true);

    return () => {
      window.removeEventListener('resize', measure);
      window.removeEventListener('scroll', measure, true);
    };
  }, [props.fitViewport, isExpanded]);

  const closeDropdown = useRef((e: MouseEvent) => {
    if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
      setIsExpanded(false);
    }
  });

  useEffect(() => {
    if (isExpanded) {
      document.addEventListener('click', closeDropdown.current);
    } else {
      document.removeEventListener('click', closeDropdown.current);
    }

    return () => {
      document.removeEventListener('click', closeDropdown.current);
    }
  }, [isExpanded]);

  const dropdownChild = React.Children.toArray(props.children).find((child) => (
    (child as React.ReactElement<any>).type === Dropdown.Content
  )) as React.ReactElement<any>;

  const triggerChild = React.Children.toArray(props.children).find((child) => (
    (child as React.ReactElement<any>).type === Dropdown.Trigger
  )) as React.ReactElement<any>;

  return (
    <div ref={containerRef} style={{ position: 'relative' }}>
      {
        cloneElement(
          triggerChild,
          {
            ...triggerChild.props,
            isExpanded,
            onClick: () => setIsExpanded(!isExpanded)
          }
        )
      }

      <div
        ref={menuRef}
        style={{
          ...(dropdownChild.props.style || {}),
          background: '#ffffff',
          border: isExpanded ? '1px solid hsl(270, 3%, 93.5%)' : 'none',
          borderRadius: 4,
          boxShadow: isExpanded ? '1px 2px 10px 0 hsl(270, 3%, 93.5%)' : 'none',
          display: isExpanded ? 'block' : 'none',
          left: align === 'left' ? 0 : 'auto',
          maxHeight: placement.maxHeight,
          minWidth: 160,
          overflowX: 'hidden',
          overflowY: placement.maxHeight ? 'auto' : 'hidden',
          position: 'absolute',
          right: align === 'right' ? 0 : 'auto',
          zIndex: 1000,
          ...(placement.openUp
            ? { bottom: '100%', marginBottom: 2 }
            : { top: '100%', marginTop: 2 })
        }}
      >
        <div>
          {
            cloneElement(
              dropdownChild,
              {
                ...dropdownChild.props,
                close: () => setIsExpanded(false)
              }
            )
          }
        </div>
      </div>
    </div>
  );
};

Dropdown.Caret = ({ isExpanded }) => {
  return (
    <div
      style={{
        borderLeft: '5px solid transparent',
        borderRight: '5px solid transparent',
        borderTop: '5px solid #B2B2B2',
        height: 0,
        width: 0
      }}
    />
  );
};

Dropdown.Chevron = ({ isExpanded }) => {
  return (
    <FontAwesomeIcon icon={isExpanded ? 'chevron-up' : 'chevron-down'} />
  );
};

Dropdown.Content = (props) => {
  if (typeof props.children === 'function') {
    return (props.children as any)({ close: props.close });
  } else {
    return props.children;
  }
};

Dropdown.Trigger = (props) => {
  return (
    <div onClick={props.onClick} style={{ cursor: 'pointer', ...(props.style || {}) }}>
      {
        (typeof props.children === 'function') ? (
          (props.children as any)({ isExpanded: props.isExpanded })
        ) : props.children
      }
    </div>
  );
};

export default Dropdown;
